import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, captureDevQueryParam } from "../api/client";
import { captureDeeplink, captureOpDay, captureOpTab } from "../utils/deeplink";
import { ApiException, type MeResponse } from "../api/types";
import { dbRoleToFront } from "./roleMap";
import type { Role } from "@ub/shared";
import { tgReady, tgLockViewport } from "../lib/telegram";

export type AuthStatus = "loading" | "authed" | "error";

export interface AuthState {
  status: AuthStatus;
  user: MeResponse | null;
  /** Roles the current user can play, deduplicated & mapped to frontend Role. */
  availableRoles: Role[];
  /** Currently active role (sticky in localStorage for multi-role users). */
  activeRole: Role | null;
  error: string | null;
  /** HTTP-статус ошибки /api/me (403 — Telegram не привязан к сотруднику), null — сеть/иное. */
  errorStatus: number | null;
  setActiveRole: (role: Role) => void;
  /** Drop the sticky active-role pick so the role-switcher shows again. */
  clearActiveRole: () => void;
  /** Force a re-fetch of /api/me. */
  refresh: () => void;
  /** Clear local auth state (used by the «Сменить аккаунт» flow). */
  signOut: () => void;
}

export const AuthContext = createContext<AuthState | null>(null);

const ACTIVE_ROLE_KEY = "ub_active_role";

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<MeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [activeRole, setActiveRoleState] = useState<Role | null>(null);

  const load = useCallback(async () => {
    setStatus("loading");
    setError(null);
    setErrorStatus(null);
    try {
      captureDevQueryParam();
      const hasDeeplink = captureDeeplink(); // TG-кнопки «Показать бронь»/«Информация клиента» — до потери query роутером
      const hasOpDay = [captureOpDay(), captureOpTab()].some(Boolean);   // 28.09: «Посмотреть» админа зала — кабинет на дне; 13.10: «заполни график» — на вкладке
      tgReady();
      tgLockViewport(); // expand + stop TG swipe-to-close so crop drag doesn't kick the operator out
      const me = await api<MeResponse>("/api/me");
      setUser(me);

      const front = uniqueRoles(me.roles.map(dbRoleToFront));
      const saved = localStorage.getItem(ACTIVE_ROLE_KEY) as Role | null;
      let initial = saved && front.includes(saved) ? saved : front.length === 1 ? front[0]! : null;
      /* 10.09 (владелец): кнопка из Telegram ведёт к конкретной брони, а бронь открывается
         в диспетчерской сетке. Сохранённая роль тут ни при чём — иначе человека уносило
         на последний экран, и ни бронь, ни карточка клиента не открывались. */
      if (hasDeeplink && front.includes("DC")) initial = "DC";
      else if (hasOpDay && front.includes("OP")) initial = "OP";
      setActiveRoleState(initial);

      setStatus("authed");
    } catch (err) {
      const msg = err instanceof ApiException ? err.message : (err as Error).message;
      setError(msg || "Не удалось загрузить пользователя");
      /* 03.10 (владелец, кадры): 403 — «не зарегистрирован» (нет строки staff или уволен) —
         экран «Нет доступа» со своим Telegram ID вместо «НЕТ ПОДКЛЮЧЕНИЯ» */
      setErrorStatus(err instanceof ApiException ? err.status : null);
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const setActiveRole = useCallback((role: Role) => {
    localStorage.setItem(ACTIVE_ROLE_KEY, role);
    setActiveRoleState(role);
  }, []);

  const clearActiveRole = useCallback(() => {
    localStorage.removeItem(ACTIVE_ROLE_KEY);
    setActiveRoleState(null);
  }, []);

  const signOut = useCallback(() => {
    localStorage.removeItem(ACTIVE_ROLE_KEY);
    localStorage.removeItem("ub_dev_staff_id");
    localStorage.removeItem("ub_auth_token");
    setUser(null);
    setActiveRoleState(null);
    void load();
  }, [load]);

  const availableRoles = useMemo(
    () => (user ? uniqueRoles(user.roles.map(dbRoleToFront)) : []),
    [user],
  );

  const value: AuthState = {
    status,
    user,
    availableRoles,
    activeRole,
    error,
    errorStatus,
    setActiveRole,
    clearActiveRole,
    refresh: load,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function uniqueRoles(roles: Role[]): Role[] {
  const seen = new Set<Role>();
  const out: Role[] = [];
  for (const r of roles) {
    if (!seen.has(r)) {
      seen.add(r);
      out.push(r);
    }
  }
  return out;
}
